import React, { useState } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import axios from 'axios';
import type { FormikHelpers, FormikProps } from 'formik';
import { Formik, useFormikContext } from 'formik';
import { isEqual, snakeCase } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import {
  useFormikValidationErrorToast,
  ValidationErrorToastContainer,
} from '~/components/hooks/useFormikValidationErrorToast';
import {
  hasAuthoredCondition,
  validateConditionsTree,
  withoutRqbIds,
} from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder';
import type { FieldDefinitionDto } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/types';
import TextFieldFormik from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';

import PolicyCategoryConditionsQueryBuilderFormik from './PolicyCategoryConditionsQueryBuilderFormik';
import type { PolicyCategoryRow } from './types';

interface FormValues {
  display_name: string;
  key: string;
  conditions_json: RuleGroupType | null;
}

// The key is what the configuration blob stores and what "Limit to Categories" matches on,
// alongside blob incident-category ids — hold a key typed on this screen to the same shape
// `snakeCase` derives. Create-time only: nothing else in the namespace enforces the shape
// (`CreatePolicyCategoryParams.key` is any 1-128 string, and the FNOL screen's own Key field
// carries no pattern), so a stored key may legitimately not match it — and since the key is
// immutable and never sent on PATCH, checking it in edit mode would block an edit, a pure
// rename included, on a field the form has disabled.
const buildValidationSchema = (isEdit: boolean, reservedKeys: string[]) => {
  const key = Yup.string().required('Required').max(128);
  return Yup.object().shape({
    display_name: Yup.string().required('Required').max(128),
    key: isEdit
      ? key
      : key
          .matches(/^[a-z0-9_]+$/, 'Key must use lowercase letters, digits and underscores only')
          .notOneOf(reservedKeys, 'This key is reserved'),
  });
};

/** The fields this dialog reads, which a per-configuration and a global category row both carry. */
type CategoryRow = Pick<PolicyCategoryRow, 'id' | 'key' | 'display_name' | 'conditions_json'>;

interface PolicyCategoryDialogProps {
  /** The categories collection route. A row is edited at `/<id>` below it and the condition
   *  catalog is read from `/condition_fields` below it, for per-configuration and global
   *  categories alike. */
  categoriesUrl: string;
  /** What the title calls the row, e.g. "Global Category". */
  entityLabel?: string;
  /** Shown by the condition builder when the catalog has no fields. */
  emptyCatalogMessage: string;
  /** null = create; a row = edit (key immutable, not sent on PATCH). */
  category: CategoryRow | null;
  /** Keys the caller's backend refuses on create. */
  reservedKeys?: string[];
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}

const PolicyCategoryDialog: React.FC<PolicyCategoryDialogProps> = ({
  categoriesUrl,
  entityLabel = 'Category',
  emptyCatalogMessage,
  category,
  reservedKeys = [],
  onClose,
  onSaved,
}) => {
  const [conditionFields, setConditionFields] = useState<FieldDefinitionDto[]>([]);

  const initialValues: FormValues = {
    display_name: category?.display_name ?? '',
    key: category?.key ?? '',
    conditions_json: category?.conditions_json ?? null,
  };

  // A stored tree the configurator never touched is neither re-sent nor re-checked. The BE
  // re-validates every path it receives, so a condition referencing a configured field that
  // has since been removed would block even a pure rename forever — while the evaluator
  // deliberately treats a vanished path as a non-match, so such rows are expected to exist.
  // The PATCH schema's `conditions_json` is optional precisely for this.
  const isTreeUnchanged = (values: FormValues) =>
    !!category &&
    !!values.conditions_json &&
    !!category.conditions_json &&
    isEqual(withoutRqbIds(values.conditions_json), withoutRqbIds(category.conditions_json));

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    try {
      if (category) {
        // `key` is immutable and the PATCH schema rejects unexpected fields (extra='forbid'),
        // so the payload must never include it.
        await axios.patch(`${categoriesUrl}/${category.id}`, {
          display_name: values.display_name,
          ...(isTreeUnchanged(values) ? {} : { conditions_json: values.conditions_json }),
        });
      } else {
        await axios.post(categoriesUrl, {
          key: values.key,
          display_name: values.display_name,
          conditions_json: values.conditions_json,
        });
      }
      await onSaved();
      onClose();
    } catch (error) {
      setSubmitting(false);
      await reportAxiosError(error);
    }
  };

  const validate = (values: FormValues) => {
    // The tree the PATCH omits must not gate the save either.
    if (isTreeUnchanged(values)) {
      return {};
    }
    // BE contract: a category with no condition would match every policy — required. The
    // check is for an authored leaf *anywhere*, not a non-empty root: a lone "+ Group"
    // click leaves `{and: [{and: []}]}`, which a root-level check calls authored and the
    // compiler turns into a bare `true`.
    if (!hasAuthoredCondition(values.conditions_json)) {
      return { conditions_json: 'At least one condition is required' };
    }
    const treeError = validateConditionsTree(values.conditions_json, conditionFields);
    return treeError ? { conditions_json: treeError } : {};
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={buildValidationSchema(!!category, reservedKeys)}
      validate={validate}
    >
      <PolicyCategoryDialogInner
        categoriesUrl={categoriesUrl}
        entityLabel={entityLabel}
        emptyCatalogMessage={emptyCatalogMessage}
        isEdit={!!category}
        onClose={onClose}
        onFieldsLoaded={setConditionFields}
      />
    </Formik>
  );
};

interface PolicyCategoryDialogInnerProps {
  categoriesUrl: string;
  entityLabel: string;
  emptyCatalogMessage: string;
  isEdit: boolean;
  onClose: () => void;
  onFieldsLoaded: (fields: FieldDefinitionDto[]) => void;
}

const PolicyCategoryDialogInner: React.FC<PolicyCategoryDialogInnerProps> = ({
  categoriesUrl,
  entityLabel,
  emptyCatalogMessage,
  isEdit,
  onClose,
  onFieldsLoaded,
}) => {
  const { handleSubmit, isSubmitting, setFieldValue, values }: FormikProps<FormValues> = useFormikContext<FormValues>();
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  // Once the key has been typed by hand the name stops driving it — otherwise the next
  // keystroke in Display Name silently discards what the user entered.
  const [isKeyEdited, setIsKeyEdited] = useState(false);

  // The conditions error lives on the whole tree, not on a focusable input — without the
  // toast, a blocked Save is indistinguishable from a dead button.
  useFormikValidationErrorToast({
    toastId: 'policy-category-errors',
    title: 'Please fix the following before saving:',
  });

  return (
    <CardDialog
      title={isEdit ? `Edit ${entityLabel} - ${values.display_name}` : `Add ${entityLabel}`}
      isDialog
      open
      maxWidth="md"
      fullWidth
      onClose={onClose}
      preventClose={isSubmitting}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSubmit}
          onClickSecondary={onClose}
          primaryLabel="Save"
          showPrimary
          secondaryLabel="Cancel"
          disabled={isSubmitting || isCatalogLoading}
        />
      }
    >
      <ValidationErrorToastContainer containerId="policy-category-errors" />
      <div className="flex flex-col gap-24">
        <div className="grid grid-cols-2 gap-24">
          <TextFieldFormik
            id="display_name"
            label="Display Name"
            fullWidth
            // @ts-expect-error TextFieldFormik is an untyped JSX module, so its `onChange` prop is
            // inferred as the `undefined` default it destructures. Fix the types when the component
            // is converted.
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue('display_name', event.target.value);
              // The key mirrors the name until the category exists; afterwards it is
              // immutable (the blob references it). A hand-typed key ends the mirroring.
              if (!isEdit && !isKeyEdited) {
                setFieldValue('key', snakeCase(event.target.value));
              }
            }}
          />
          <TextFieldFormik
            id="key"
            label="Key"
            fullWidth
            disabled={isEdit}
            // @ts-expect-error see the `onChange` note above.
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setIsKeyEdited(true);
              setFieldValue('key', event.target.value);
            }}
          />
        </div>
        <PolicyCategoryConditionsQueryBuilderFormik
          catalogUrl={`${categoriesUrl}/condition_fields`}
          emptyMessage={emptyCatalogMessage}
          onFieldsLoaded={onFieldsLoaded}
          onLoadingChange={setIsCatalogLoading}
        />
      </div>
    </CardDialog>
  );
};

export default PolicyCategoryDialog;
